/**
 * Personal_Info_Screen.tsx — SK Platform
 * "Tell us about you" — the LEGACY profile-completion card.
 *
 * NOT part of the current signup flow. Signup collects the whole profile in one
 * form (see screens/auth/signup.tsx) and the account is created complete, so a
 * user who signs up today never sees this screen.
 *
 * It survives for accounts that still have a blank profile: those created
 * before the single flow, and invite-accepts. Login and the boot probe route
 * here on `profile_complete === false`, which is the only way in.
 *
 * Collects name + company + mobile and calls completeProfile() — which hits the
 * same /v1/auth/signup endpoint, now authenticated, so the server treats it as
 * profile completion. "Go Live" then drops the user into the app on Settings.
 *
 * NOTE: this screen still asks for the org's COUNTRY (tax/currency) and not the
 * business REGION (data residency). That is correct — an account reaching this
 * card already exists, so its region was fixed when it was created and cannot
 * be chosen here.
 *
 * All API calls go through Auth_Service → Api_Endpoints (no hardcoded URLs).
 */

import React, { useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, Platform, Animated } from 'react-native';

// ── Components ────────────────────────────────────────────────────────────
import CommonInput from '../../components/commonInput';
import CountrySelect from '../../components/countrySelect';
import MobileNumberInput, { COUNTRIES, CountryEntry } from '../../components/mobileNumberInput';
import PrimaryButton from '../../components/primaryButton';

// ── Navigation ──────────────────────────────────────────────────────────────
import { CONTENT } from '../../utils/responsive';
import { clearAuthDeepLink } from '../../navigation/linking';
import { useAuthFlow } from '../../navigation/authFlowContext';

// ── Service layer ─────────────────────────────────────────────────────────
import { completeProfile } from '../../services/auth/auth';

// ── Handlers ──────────────────────────────────────────────────────────────
import { handleApiError } from '../../utils/errorHandler';
import { showSuccess } from '../../utils/successHandler';
import { useToast } from '../../components/toast';

// ── Schema types ──────────────────────────────────────────────────────────
import { CompleteProfilePayload } from '../../schema/auth/signup';

// ── Validations ───────────────────────────────────────────────────────────
import * as SignupValidations from '../../validations/signupValidations';

// ── Theme ─────────────────────────────────────────────────────────────────
import { useTheme } from '../../context/themeContext';
import AuthBrandHeader from '../../components/authBrandHeader';

const Personal_Info_Screen: React.FC = () => {
  const { theme } = useTheme();
  const { colors } = theme;
  const toast = useToast();
  const { enterApp } = useAuthFlow();
  const defaultCountry = COUNTRIES.find((c) => c.code === 'IN') || COUNTRIES[0];

  const [form, setForm] = useState({
    firstName:       '',
    lastName:        '',
    businessName:    '',
    mobile:          '',
    // The PHONE's country — drives country_code (the dial code) only.
    selectedCountry: defaultCountry,
    // The ORGANISATION's registered country — its legal/compliance home, and
    // what sk-billing turns into a tax pack + base currency. Seeded from the
    // phone flag so the common case needs no extra tap, but it is a separate
    // fact and the user can change either independently.
    orgCountry: defaultCountry,
  });

  const [touched, setTouched] = useState({
    firstName:    false,
    lastName:     false,
    businessName: false,
    mobile:       false,
  });

  const [isLoading, setIsLoading] = useState(false);

  // ── Entrance animation (fade + gentle rise) ───────────────────────────
  const fade = useRef(new Animated.Value(0)).current;
  const rise = useRef(new Animated.Value(14)).current;
  useEffect(() => {
    Animated.parallel([
      Animated.timing(fade, { toValue: 1, duration: 280, useNativeDriver: true }),
      Animated.spring(rise, { toValue: 0, friction: 8, tension: 64, useNativeDriver: true }),
    ]).start();
  }, [fade, rise]);

  /* ── Inline error resolvers ───────────────────────────────────────── */
  const getFirstNameError    = () => SignupValidations.getFirstNameError(form.firstName, touched.firstName);
  const getLastNameError     = () => SignupValidations.getLastNameError(form.lastName, touched.lastName);
  const getBusinessNameError = () => SignupValidations.getBusinessNameError(form.businessName, touched.businessName);
  const getMobileError       = () => SignupValidations.getMobileError(form.mobile, touched.mobile, form.selectedCountry);

  const profileError = (): string | null =>
    getFirstNameError() ||
    getLastNameError() ||
    getBusinessNameError() ||
    SignupValidations.getMobileError(form.mobile, true, form.selectedCountry) ||
    null;

  // Filter input to an allowed set; toast exactly which character isn't allowed.
  const filterNames = (raw: string) => {
    const bad = raw.match(/[^A-Za-z\s]/);
    if (bad) toast.error(`“${bad[0]}” is not allowed — letters only`);
    return raw.replace(/[^A-Za-z\s]/g, '');
  };
  const filterCompany = (raw: string) => {
    const bad = raw.match(new RegExp(SignupValidations.BUSINESS_NAME_DISALLOWED.source));
    if (bad) toast.error(`“${bad[0]}” is not allowed in company name`);
    return raw.replace(SignupValidations.BUSINESS_NAME_DISALLOWED, '');
  };

  /* ── Submit — complete profile + go live ───────────────────────────── */
  const handleGoLive = async () => {
    if (isLoading) return;

    setTouched({ firstName: true, lastName: true, businessName: true, mobile: true });
    const err = profileError();
    if (err) {
      toast.error(err);
      return;
    }

    setIsLoading(true);
    try {
      const payload: CompleteProfilePayload = {
        first_name:    form.firstName,
        last_name:     form.lastName,
        business_name: form.businessName,
        country:       form.orgCountry.code,          // ISO-3166-1 alpha-2 — the ORG's country
        country_code:  form.selectedCountry.dialCode, // "+91", etc.
        phone_number:  form.mobile,
      };

      const res = await completeProfile(payload);
      showSuccess(res, "You're all set!", 'Welcome to SK Platform — your workspace is ready.');
      // Drop the ?mode/?token query (we may have arrived via the verify link),
      // then enter the app on Settings as the first-run landing.
      clearAuthDeepLink();
      enterApp({ initialShellRoute: { area: 'tenant', screen: 'org' } });
    } catch (error: any) {
      handleApiError(error, 'Could not save your details', 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={[s.container, { backgroundColor: colors.paper }]}>
      <ScrollView
        contentContainerStyle={s.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        <View style={[s.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
          <AuthBrandHeader />

          <Text style={[s.title, { color: colors.ink }]}>Tell us about you</Text>
          <Text style={[s.subtitle, { color: colors.ink3 }]}>
            Add your name, company, and mobile number so we can personalize your workspace.
          </Text>

          <Animated.View style={[s.form, { opacity: fade, transform: [{ translateY: rise }] }]}>
            {/* First name + Last name */}
            <View style={s.row}>
              <View style={[s.col, { marginRight: 8 }]}>
                <CommonInput
                  label="First name"
                  required
                  placeholder="e.g. Gokulakrishnan"
                  value={form.firstName}
                  onChangeText={(t: string) => setForm({ ...form, firstName: filterNames(t) })}
                  touched={touched.firstName}
                  error={getFirstNameError()}
                  inputProps={{ onBlur: () => setTouched((t) => ({ ...t, firstName: true })) }}
                />
              </View>
              <View style={[s.col, { marginLeft: 8 }]}>
                <CommonInput
                  label="Last name"
                  required
                  placeholder="e.g. A"
                  value={form.lastName}
                  onChangeText={(t: string) => setForm({ ...form, lastName: filterNames(t) })}
                  touched={touched.lastName}
                  error={getLastNameError()}
                  inputProps={{ onBlur: () => setTouched((t) => ({ ...t, lastName: true })) }}
                />
              </View>
            </View>

            {/* Company name */}
            <CommonInput
              label="Company Name"
              required
              placeholder="e.g. Usher Media"
              value={form.businessName}
              onChangeText={(t: string) => setForm({ ...form, businessName: filterCompany(t) })}
              touched={touched.businessName}
              error={getBusinessNameError()}
              inputProps={{ onBlur: () => setTouched((t) => ({ ...t, businessName: true })) }}
            />

            {/* Country — the ORG's registered country. Sits above the phone
                field because it is the more consequential of the two: every
                product derives its tax regime and currency from it, and those
                are immutable once the org's billing profile activates. */}
            <CountrySelect
              required
              value={form.orgCountry}
              onChange={(c: CountryEntry) => setForm({ ...form, orgCountry: c })}
              hint="sets your tax & currency"
            />

            {/* Mobile number */}
            <View>
              <MobileNumberInput
                required
                value={form.mobile}
                selectedCountry={form.selectedCountry}
                onCountryChange={(c: CountryEntry) => setForm({ ...form, selectedCountry: c, mobile: '' })}
                onChangeText={(t: string) => setForm({ ...form, mobile: t })}
                error={getMobileError()}
                onBlur={() => setTouched((t) => ({ ...t, mobile: true }))}
              />
            </View>

            {/* Go Live */}
            <View style={s.actionWrap}>
              <PrimaryButton
                title="Let's Get Started  →"
                onPress={handleGoLive}
                loading={isLoading}
                hoverLift
                style={s.goLiveBtn}
                textStyle={s.goLiveBtnText}
              />
            </View>
          </Animated.View>
        </View>
      </ScrollView>
    </View>
  );
};

/* ── Styles ────────────────────────────────────────────────────────────── */
const s = StyleSheet.create({
  container: {
    flex: 1,
    ...(Platform.OS === 'web' ? { minHeight: '100vh' } as any : {}),
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: 28,
    alignItems: 'center',
    paddingHorizontal: 16,
  },
  card: {
    width: '100%',
    maxWidth: CONTENT.form,
    borderRadius: 24,
    borderWidth: 1,
    paddingHorizontal: 30,
    paddingTop: 30,
    paddingBottom: 26,
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? ({
          boxShadow:
            '0 24px 60px -18px rgba(15,23,42,0.22), 0 6px 18px -8px rgba(15,23,42,0.10)',
        } as any)
      : { shadowColor: '#0F172A', shadowOffset: { width: 0, height: 14 }, shadowOpacity: 0.14, shadowRadius: 32, elevation: 10 }),
  },
  accentBar: { position: 'absolute', top: 0, left: 0, right: 0, height: 4 },
  logoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
    gap: 10,
  },
  logoMark: {
    width: 38,
    height: 38,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 6px 14px rgba(15,23,42,0.28)' } as any)
      : { shadowColor: '#0F172A', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.25, shadowRadius: 8, elevation: 4 }),
  },
  logoText: { fontWeight: '800', fontSize: 15, letterSpacing: -0.5 },
  logoName: {
    fontSize: 24,
    fontWeight: '900',
    letterSpacing: -0.8,
    ...(Platform.OS === 'web' ? ({ textShadow: '0 1px 1px rgba(15,23,42,0.10)' } as any) : {}),
  },
  taglinePill: {
    alignSelf: 'center',
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 5,
    marginBottom: 20,
  },
  tagline: { fontSize: 10, fontWeight: '800', textAlign: 'center', letterSpacing: 1.6 },
  title: {
    fontSize: 30,
    fontWeight: '900',
    textAlign: 'center',
    letterSpacing: -0.8,
    marginBottom: 10,
  },
  subtitle: {
    fontSize: 13.5,
    fontWeight: '500',
    marginBottom: 28,
    textAlign: 'center',
    lineHeight: 21,
    paddingHorizontal: 6,
  },
  form:       { width: '100%' },
  row:        { flexDirection: 'row', width: '100%' },
  col:        { flex: 1 },
  actionWrap: { marginTop: 18 },
  goLiveBtn: {
    height: 52,
    minHeight: 52,
    borderRadius: 14,
    ...(Platform.OS === 'web'
      ? ({ boxShadow: '0 14px 28px rgba(11,11,13,0.32)' } as any)
      : { shadowColor: '#0B0B0D', shadowOffset: { width: 0, height: 12 }, shadowOpacity: 0.32, shadowRadius: 22, elevation: 10 }),
  },
  goLiveBtnText: { fontSize: 15, letterSpacing: 1.2 },
});

export default Personal_Info_Screen;
